import {Observable, Subject, timer} from 'rxjs';
import {filter, share, switchMap, takeUntil, tap} from 'rxjs/operators';
import {dependOn} from '../../../common/rxjs/depend-on';
import {squash} from '../../../common/rxjs/squash';
import {WIDGET_UPDATES_GATING$} from '../../../components/layout/widget/widget-updates-gating';
import {getPositionIdentificationKey, Position} from '../../../components/position';
import {PositionsStreamingService} from './positions-streaming.service';

/**
 * Includes solution against the bug: [ECN-261] Outdated positions after reconnect.
 */
export const getSquashedPositionsStream = (
    params: {
        positionsStreamingService: PositionsStreamingService;
        filterBy?: (position: Position) => boolean;
        onPositions?: (positions: Position[], cleanUpBeforeProcessing: boolean) => void;
        onPosition?: (position: Position, cleanUpBeforeProcessing: boolean) => void;
        onCleanUp: (isNoFollowupPositions: boolean) => void;
    }
): Observable<Position[]> => {
    const UPDATE_TIMEOUT_BEFORE_CLEANUP = 500;

    let doCleanUpPositionsOnNextUpdate: boolean = false;
    const updateReceived$ = new Subject<void>();

    const position$ = params.positionsStreamingService.position$.pipe(
        filter(position => !!position),
        squash({
            closingNotifier: WIDGET_UPDATES_GATING$,
            filterBy:        (position: Position) => !params.filterBy || params.filterBy(position),
            groupBy:         getPositionIdentificationKey,
            spread:          false
        }),
        tap((positions: Position[]) => {
            const doRemovePrevious = doCleanUpPositionsOnNextUpdate;
            doCleanUpPositionsOnNextUpdate = false;

            if (doRemovePrevious) {
                params.onCleanUp(false);
            }

            if (params.onPosition) {
                if (positions.length === 1) {
                    params.onPosition(positions[0], doRemovePrevious);
                } else {
                    positions.forEach(position => params.onPosition(position, doRemovePrevious));
                }
            }

            if (params.onPositions) {
                params.onPositions(positions, doRemovePrevious);
            }
        })
    );

    const cleanUpPositions$ = params.positionsStreamingService.cleanUpPositions$.pipe(
        tap(() => {
            doCleanUpPositionsOnNextUpdate = true;
        }),
        switchMap(() =>
            timer(UPDATE_TIMEOUT_BEFORE_CLEANUP).pipe(
                tap(() => {
                    if (doCleanUpPositionsOnNextUpdate) {
                        doCleanUpPositionsOnNextUpdate = false;
                        params.onCleanUp(true);
                    }
                }),
                takeUntil(updateReceived$)
            )
        )
    );

    return position$.pipe(
        dependOn(cleanUpPositions$),
        share()
    );
};
